import styled from '@emotion/styled';
import { ObjectId } from '@racemap/sdk/schema/base';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { Space } from 'antd';
import { type FC, useEffect } from 'react';
import { useFilteredTrackers, useIsMobile, useSampleTrackers } from '../../../lib/customHooks';
import { useStore } from '../../../store/reducers';
import { HStack } from '../../BasicComponents/MetaComponent';
import { IconArrowDoubleDown, IconArrowDoubleUp, IconClose, IconSpinner } from '../../Icon';
import { NoDataPlaceholder } from '../../Prediction/NoDataPlaceholder';
import { Column } from '../Layout/Column';
import { TopBar } from '../Layout/TopBar';
import { TrackerTable } from './TrackerTable';
interface Props {
  onMoveToSample: () => void;
  onRemoveFromSample: () => void;
}

const SELECTION_ID = 'total';

export const TotalTrackerTable: FC<Props> = ({ onMoveToSample, onRemoveFromSample }) => {
  const trackers = useFilteredTrackers();
  const selectedTrackerIds = useStore((s) => s.trackers.selectedTrackerIds[SELECTION_ID]);
  const sampleTrackerIds = useStore((s) => s.trackers.sampleTrackerIds);
  const isLoading = useStore(
    (s) => s.trackers.loadingState === 'LOADING' && s.trackers.lastTrackerLoadedAt != null,
  );

  const { updateCompleteSelection, removeAllTrackersFromSample, deselectTrackers } = useStore(
    (s) => s.trackers.actions,
  );

  const { resetFilterSelection } = useStore((s) => s.trackers.actions);
  const isMobile = useIsMobile();
  const sampleTrackersLenght = useSampleTrackers().length;
  const filterSet = useStore((s) => s.trackers.filterSet);
  const isSomethingSelected = filterSet.size > 0;

  const hanldeDeselecteAllTrackers = () => {
    const trackerIdsArray = Array.from(sampleTrackerIds).map((id) => new ObjectId(id));
    deselectTrackers(trackerIdsArray, 'sample');
    removeAllTrackersFromSample();
  };

  useEffect(() => {
    if (isMobile) {
      hanldeDeselecteAllTrackers();
    }
  }, [isMobile]);

  return (
    <Column
      topBarContent={
        isMobile ? null : (
          <Bar>
            <FilteredTrackerTag>
              <Space>
                <div>{trackers.length} filtered</div>
                {isSomethingSelected && <IconClose onClick={resetFilterSelection} />}
                {isLoading && <IconSpinner />}
              </Space>
            </FilteredTrackerTag>
            <ArrowButtons>
              <Button onClick={() => onMoveToSample()} disabled={selectedTrackerIds.size === 0}>
                <IconArrowDoubleUp />
              </Button>
              <Button onClick={() => onRemoveFromSample()} disabled={sampleTrackerIds.size === 0}>
                <IconArrowDoubleDown />
              </Button>
            </ArrowButtons>
            <SelectedTrackerTag>
              {sampleTrackersLenght} selected <IconClose onClick={hanldeDeselecteAllTrackers} />
            </SelectedTrackerTag>
          </Bar>
        )
      }
    >
      <TrackerTable
        trackers={trackers}
        noDataPlaceholder={<NoTrackerPlaceholder />}
        selectedTrackerIds={selectedTrackerIds}
        onSelectionChange={(newIds) => updateCompleteSelection(newIds, SELECTION_ID)}
        dragElementType="total-trackers"
        disabledTrackerIds={sampleTrackerIds}
      />
    </Column>
  );
};

const NoTrackerPlaceholder = () => {
  return (
    <NoDataPlaceholder title="No Tracker" subtitle="Add tracker per + in top bar or csv upload!" />
  );
};

const Bar = styled(TopBar)`
  display: grid;
  grid-template-columns: 150px auto 150px;
  justify-content: space-between;
`;

const SelectedTrackerTag = styled.div`
    border-radius: 0 0 5px 5px;
    margin: 0 10px 15px 0;
    padding: 0 10px 0 10px;
    background-color: ${RacemapColors.CloudBlue};
    text-align: center;
    `;

const FilteredTrackerTag = styled.div`
    border-radius: 5px 5px 0 0;
    margin: 15px 0 0 10px;
    padding: 0 10px 0 10px;
    background-color: ${RacemapColors.CloudBlue};
    text-align: center;
`;

const ArrowButtons = styled(HStack)`
  height: 100%;
`;

const Button = styled.button<{ disabled: boolean }>`
  width: 100px;
  margin: 0 5px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: unset;
  border-radius: unset;
  border-bottom: unset;
  border-right: solid 1px ${RacemapColors.LightGray};
  border-left: solid 1px ${RacemapColors.LightGray};
  background: white;

  ${({ disabled }) =>
    disabled
      ? ` 
    opacity: 0.5;
    cursor: not-allowed;

    svg {
      cursor: not-allowed;
    }
    `
      : ''}

  :hover {
    background: ${RacemapColors.LightGray};
  }
`;
